import {
    areNumbersWithinTolerance,
    inchesToMm,
    mmToInches,
    organisationDimensionTolerance,
    organisationLengthPrecision,
} from '../GeneralUtils/GeneralUtils'

// Constants
const PROFILE_SIZE_TOLERANCE_PERCENTAGE = 2
const UNIT_CONVERSIONS = {
    MM_TO_M: 1000,
    INCHES_TO_FEET: 12,
}

// Profile types
export const PROFILE_TYPES = {
    RECTANGLE: 'Rectangle',
    SQUARE: 'Square',
    CIRCLE: 'Circle',
    FLAT_SIDED_OVAL: 'FlatSidedOval',
    ANGLE: 'Angle',
    U_CHANNEL: 'UChannel',
    I_BEAM: 'IBeam',
    T_BEAM: 'TBeam',
}

/**
 * Converts dimensions to base units based on unit system
 * @param {Object} dimensions - Object containing dimensional values
 * @param {boolean} useImperialUnits - Whether to use imperial units
 * @returns {Object} Converted dimensions
 */
const convertToBaseUnits = (dimensions, useImperialUnits) => {
    const divisor = useImperialUnits ? UNIT_CONVERSIONS.INCHES_TO_FEET : UNIT_CONVERSIONS.MM_TO_M
    const converted = {}

    Object.entries(dimensions).forEach(([key, value]) => {
        const numericValue = typeof value === 'number' ? value : Number(value)
        if (isNaN(numericValue)) {
            converted[key] = 0
        } else {
            converted[key] = numericValue / divisor
        }
    })

    return converted
}

/**
 * Calculates sheet volume
 * @param {number} thickness - Sheet thickness
 * @param {number} width - Sheet width
 * @param {number} height - Sheet height
 * @param {boolean} useImperialUnits - Whether to use imperial units
 * @returns {number} Volume in cubic units
 */
const sheetVolume = (thickness, width, height, useImperialUnits) => {
    const converted = convertToBaseUnits({ thickness, width, height }, useImperialUnits)
    return converted.height * converted.width * converted.thickness
}

/**
 * Calculates rectangle (hollow) volume
 * @param {number} thickness - Wall thickness
 * @param {number} materialLength - Length of material
 * @param {number} width - Rectangle width
 * @param {number} height - Rectangle height
 * @param {boolean} useImperialUnits - Whether to use imperial units
 * @returns {number} Volume in cubic units
 */
const rectangleVolume = (thickness, materialLength, width, height, useImperialUnits) => {
    const converted = convertToBaseUnits({ thickness, materialLength, width, height }, useImperialUnits)
    const { height: h, materialLength: ml, thickness: t, width: w } = converted

    const outerArea = w * h
    const innerArea = (w - 2 * t) * (h - 2 * t)
    return ml * (outerArea - innerArea)
}

/**
 * Calculates circle (hollow) volume
 * @param {number} thickness - Wall thickness
 * @param {number} materialLength - Length of material
 * @param {number} diameter - Circle diameter
 * @param {boolean} useImperialUnits - Whether to use imperial units
 * @returns {number} Volume in cubic units
 */
const circleVolume = (thickness, materialLength, diameter, useImperialUnits) => {
    const converted = convertToBaseUnits({ thickness, materialLength, diameter }, useImperialUnits)
    const { diameter: d, materialLength: ml, thickness: t } = converted

    const outerRadius = d / 2
    const innerRadius = (d - 2 * t) / 2
    return ml * Math.PI * (Math.pow(outerRadius, 2) - Math.pow(innerRadius, 2))
}

/**
 * Calculates oval (flat-sided oval) volume
 * @param {number} thickness - Wall thickness
 * @param {number} materialLength - Length of material
 * @param {number} width - Oval width
 * @param {number} height - Oval height
 * @param {boolean} useImperialUnits - Whether to use imperial units
 * @returns {number} Volume in cubic units
 */
const ovalVolume = (thickness, materialLength, width, height, useImperialUnits) => {
    const converted = convertToBaseUnits({ thickness, materialLength, width, height }, useImperialUnits)
    const { height: h, materialLength: ml, thickness: t, width: w } = converted

    const rectangularPart = (w - h) * 2 * t
    const circularPart = Math.PI * (Math.pow(h / 2, 2) - Math.pow((h - 2 * t) / 2, 2))
    return ml * (rectangularPart + circularPart)
}

/**
 * Calculates angle (open-section) volume
 * @param {number} thickness - Leg thickness
 * @param {number} materialLength - Length of material
 * @param {number} width - Angle overall width
 * @param {number} height - Angle overall height
 * @param {boolean} useImperialUnits - Whether to use imperial units
 * @returns {number} Volume in cubic units
 */
const angleVolume = (thickness, materialLength, width, height, useImperialUnits) => {
    const converted = convertToBaseUnits({ thickness, materialLength, width, height }, useImperialUnits)
    const { height: h, materialLength: ml, thickness: t, width: w } = converted

    const crossSectionArea = (w - t) * t + h * t
    return ml * crossSectionArea
}

/**
 * Calculates U-channel (open-section) volume
 * @param {number} thickness - Leg thickness
 * @param {number} materialLength - Length of material
 * @param {number} width - Channel overall width (base)
 * @param {number} height - Channel overall height (legs)
 * @param {boolean} useImperialUnits - Whether to use imperial units
 * @returns {number} Volume in cubic units
 */
const uChannelVolume = (thickness, materialLength, width, height, useImperialUnits) => {
    const converted = convertToBaseUnits({ thickness, materialLength, width, height }, useImperialUnits)
    const { height: h, materialLength: ml, thickness: t, width: w } = converted

    const crossSectionArea = w * t + (h - t) * t * 2
    return ml * crossSectionArea
}

/**
 * Calculates T-beam (open-section) volume
 * @param {number} thickness - Flange/stem thickness
 * @param {number} materialLength - Length of material
 * @param {number} width - Flange width
 * @param {number} height - Overall depth
 * @param {boolean} useImperialUnits - Whether to use imperial units
 * @returns {number} Volume in cubic units
 */
const tBeamVolume = (thickness, materialLength, width, height, useImperialUnits) => {
    const converted = convertToBaseUnits({ thickness, materialLength, width, height }, useImperialUnits)
    const { height: h, materialLength: ml, thickness: t, width: w } = converted

    const crossSectionArea = w * t + (h - t) * t
    return ml * crossSectionArea
}

/**
 * Calculates I-beam (open-section) volume. A missing webThickness (e.g. while AddSheet
 * re-derives rate/cost on every keystroke, before the Web Thickness field is filled in)
 * resolves to 0 via convertToBaseUnits, matching how every other profile treats an
 * incomplete required dimension during live typing — form validation, not this
 * calculation, is what actually blocks submitting an I-beam with no web thickness.
 * @param {number} thickness - Flange thickness
 * @param {number} webThickness - Web thickness
 * @param {number} materialLength - Length of material
 * @param {number} width - Flange width
 * @param {number} height - Overall depth
 * @param {boolean} useImperialUnits - Whether to use imperial units
 * @returns {number} Volume in cubic units
 */
const iBeamVolume = (thickness, webThickness, materialLength, width, height, useImperialUnits) => {
    const converted = convertToBaseUnits({ thickness, webThickness, materialLength, width, height }, useImperialUnits)
    const { height: h, materialLength: ml, thickness: t, webThickness: wt, width: w } = converted

    const crossSectionArea = w * t * 2 + (h - t * 2) * wt
    return ml * crossSectionArea
}

export const sheetWeight = ({ density, height, thickness, useImperialUnits, width }) => {
    return density * sheetVolume(thickness, width, height, useImperialUnits)
}

export const rectangleWeight = ({ density, height, materialLength, thickness, useImperialUnits, width }) => {
    return density * rectangleVolume(thickness, materialLength, width, height, useImperialUnits)
}

export const circleWeight = ({ density, diameter, materialLength, thickness, useImperialUnits }) => {
    return density * circleVolume(thickness, materialLength, diameter, useImperialUnits)
}

export const ovalWeight = ({ density, height, materialLength, thickness, useImperialUnits, width }) => {
    return density * ovalVolume(thickness, materialLength, width, height, useImperialUnits)
}

export const angleWeight = ({ density, height, materialLength, thickness, useImperialUnits, width }) => {
    return density * angleVolume(thickness, materialLength, width, height, useImperialUnits)
}

export const uChannelWeight = ({ density, height, materialLength, thickness, useImperialUnits, width }) => {
    return density * uChannelVolume(thickness, materialLength, width, height, useImperialUnits)
}

export const tBeamWeight = ({ density, height, materialLength, thickness, useImperialUnits, width }) => {
    return density * tBeamVolume(thickness, materialLength, width, height, useImperialUnits)
}

export const iBeamWeight = ({ density, height, materialLength, thickness, useImperialUnits, webThickness, width }) => {
    return density * iBeamVolume(thickness, webThickness, materialLength, width, height, useImperialUnits)
}

/**
 * Calculates material weight based on profile type
 * @param {string} profile - Profile type
 * @param {number} density - Material density
 * @param {number} thickness - Material thickness
 * @param {number} materialLength - Material length
 * @param {number} width - Material width
 * @param {number} height - Material height
 * @param {boolean} useImperialUnits - Whether to use imperial units
 * @param {number} diameter - Material diameter (for circles)
 * @param {number} webThickness - Web thickness (for IBeam)
 * @returns {number} Material weight
 */
const materialWeight = (
    profile,
    density,
    thickness,
    materialLength,
    width,
    height,
    useImperialUnits,
    diameter,
    webThickness
) => {
    switch (profile) {
        case PROFILE_TYPES.RECTANGLE:
        case PROFILE_TYPES.SQUARE:
            return rectangleWeight({
                density,
                thickness,
                materialLength,
                width,
                height,
                useImperialUnits,
            })
        case PROFILE_TYPES.CIRCLE:
            return circleWeight({
                density,
                thickness,
                materialLength,
                diameter,
                useImperialUnits,
            })
        case PROFILE_TYPES.FLAT_SIDED_OVAL:
            return ovalWeight({
                density,
                thickness,
                materialLength,
                width,
                height,
                useImperialUnits,
            })
        case PROFILE_TYPES.ANGLE:
            return angleWeight({
                density,
                thickness,
                materialLength,
                width,
                height,
                useImperialUnits,
            })
        case PROFILE_TYPES.U_CHANNEL:
            return uChannelWeight({
                density,
                thickness,
                materialLength,
                width,
                height,
                useImperialUnits,
            })
        case PROFILE_TYPES.T_BEAM:
            return tBeamWeight({
                density,
                thickness,
                materialLength,
                width,
                height,
                useImperialUnits,
            })
        case PROFILE_TYPES.I_BEAM:
            return iBeamWeight({
                density,
                thickness,
                materialLength,
                width,
                height,
                useImperialUnits,
                webThickness,
            })
        default:
            return sheetWeight({
                density,
                thickness,
                width,
                height,
                useImperialUnits,
            })
    }
}

/**
 * Calculates price based on material weight and operation type
 * @param {number} value - Base price value
 * @param {string} profile - Material profile type
 * @param {Object} params - Material parameters
 * @param {number} params.density - Material density
 * @param {number} params.diameter - Material diameter (for circles)
 * @param {number} params.height - Material height
 * @param {number} params.materialLength - Material length
 * @param {number} params.thickness - Material thickness
 * @param {boolean} params.useImperialUnits - Whether to use imperial units
 * @param {number} params.width - Material width
 * @param {number} [params.webThickness] - Web thickness (for IBeam)
 * @param {'rate'|'cost'} operation - Whether to calculate rate (divide) or cost (multiply)
 * @returns {string} Calculated price to 2 decimal places
 */
const calculatePrice = (
    value,
    profile,
    { density, diameter, height, materialLength, thickness, useImperialUnits, webThickness, width },
    operation = 'rate'
) => {
    // Handle square profile (width = height)
    const effectiveHeight = profile === PROFILE_TYPES.SQUARE ? width : height

    const mWeight = materialWeight(
        profile,
        density,
        thickness,
        materialLength,
        width,
        effectiveHeight,
        useImperialUnits,
        diameter,
        webThickness
    )

    if (mWeight <= 0) {
        return '0.00'
    }

    const result = operation === 'rate' ? value / mWeight : value * mWeight
    return result.toFixed(2)
}

/**
 * Calculates rate price (value divided by weight)
 * @param {number} val - Rate value
 * @param {string} profile - Material profile type
 * @param {Object} params - Material parameters
 * @returns {string} Rate price to 2 decimal places
 */
export const calculateRatePrice = (val, profile, params) => {
    return calculatePrice(val, profile, params, 'rate')
}

/**
 * Calculates cost price (value multiplied by weight)
 * @param {number} val - Cost value
 * @param {string} profile - Material profile type
 * @param {Object} params - Material parameters
 * @returns {string} Cost price to 2 decimal places
 */
export const calculateCostPrice = (val, profile, params) => {
    return calculatePrice(val, profile, params, 'cost')
}

/**
 * Extracts part dimensions from a part object
 * @param {Object} part - Part object
 * @returns {Object|null} Part dimensions object or null if part is invalid
 */
export const getPartDimensions = (part) => {
    if (!part) return null
    return {
        profile: part.profile,
        displayBoundBoxHeight: part.displayBoundBoxHeight,
        displayBoundBoxWidth: part.displayBoundBoxWidth,
        rotaryProfileLength: part.rotaryProfileLength,
        rotaryProfileWidth: part.rotaryProfileWidth,
        diameter: part.diameter,
        isImperial: part.isImperial,
    }
}

/**
 * Gets formatted display string for part dimensions. The default branch (Rectangle,
 * FlatSidedOval, Angle, UChannel) always encodes as "Width x Height" in that fixed
 * order — see parseProfileDimensions for the inverse.
 * @param {Object} part - Part object
 * @param {Function} dimensionFormat - Function to format dimensions
 * @returns {string} Formatted dimension string, or '' if the part/values are invalid
 */
export const getPartDimensionsDisplay = (part, dimensionFormat) => {
    if (!part || !part.profile || typeof dimensionFormat !== 'function') return ''
    switch (part.profile.toLowerCase()) {
        case 'square':
            return part.rotaryProfileWidth ? dimensionFormat(part.rotaryProfileWidth) : ''
        case 'circle':
            return part.diameter ? dimensionFormat(part.diameter) : ''
        default:
            return part.rotaryProfileWidth && part.rotaryProfileLength
                ? `${dimensionFormat(part.rotaryProfileWidth)} x ${dimensionFormat(part.rotaryProfileLength)}`
                : ''
    }
}

/**
 * Gets formatted display string for sheet/record dimensions. Same "Width x Height"
 * fixed-order contract as getPartDimensionsDisplay.
 * @param {Object} sheet - Sheet object
 * @param {Function} dimensionFormat - Function to format dimensions
 * @returns {string} Formatted dimension string, or '' if the sheet/values are invalid
 */
export const getRecordDimensionsDisplay = (sheet, dimensionFormat) => {
    if (!sheet || !sheet.profile || typeof dimensionFormat !== 'function') return ''
    switch (sheet.profile.toLowerCase()) {
        case 'square':
            return sheet.sheetWidth ? dimensionFormat(sheet.sheetWidth) : ''
        case 'circle':
            return sheet.diameter ? dimensionFormat(sheet.diameter) : ''
        default:
            return `${dimensionFormat(sheet.sheetWidth)} x ${dimensionFormat(sheet.sheetHeight)}`
    }
}

/**
 * Formats a record's thickness for display. An IBeam has two: the flange thickness in
 * `thickness` and the web thickness, which is the only value distinguishing two otherwise
 * identical beam records. Renders them as "web/flange" — web first, matching the steel-section
 * convention where tw precedes tf. Every other profile renders its single thickness unchanged,
 * so call sites can adopt this unconditionally.
 *
 * Deliberately separate from getRecordDimensionsDisplay: that encodes a dropdown value that
 * parseProfileDimensions decodes back onto quote-item fields, and must not gain a term. This
 * mirrors the quote export (QM-3685), which added a discrete "Web thickness" CSV column and
 * left the dimension string untouched.
 * @param {Object} sheet - Sheet/record object (profile, thickness, webThickness)
 * @param {Function} dimensionFormat - Number formatter (the `n` from useNumberFormatter)
 * @param {Object} [options]
 * @param {number} [options.thickness] - Overrides sheet.thickness, for sites that show the
 *   part's own flange thickness against the record's web thickness
 * @param {number} [options.webThickness] - Fallback while DtoUtils.CreateSheetDto does not map
 *   the nested field; pass the quote item's top-level webThickness
 * @returns {string} e.g. '8/12' for an IBeam, '12' otherwise, or '' when there is no
 *   thickness to show (null/undefined sheet, or a missing/blank resolved thickness)
 */
export const getThicknessDisplay = (sheet, dimensionFormat, { thickness, webThickness } = {}) => {
    if (!sheet) return ''

    const resolvedThickness = thickness ?? sheet.thickness
    const resolvedWebThickness = sheet.webThickness ?? webThickness

    if (!resolvedThickness) return ''

    if (!hasWebThickness(sheet, { webThickness })) {
        return dimensionFormat(resolvedThickness)
    }

    return `${dimensionFormat(resolvedWebThickness)}/${dimensionFormat(resolvedThickness)}`
}

/**
 * Whether a record carries a second, web thickness: only an IBeam does, and only once the value
 * is known. Callers use it to caption the "web/flange" pair that getThicknessDisplay renders.
 * @param {Object} sheet - Sheet/record object (profile, webThickness)
 * @param {Object} [options]
 * @param {number} [options.webThickness] - Fallback for when the nested sheet omits it
 * @returns {boolean}
 */
export const hasWebThickness = (sheet, { webThickness } = {}) =>
    sheet?.profile === PROFILE_TYPES.I_BEAM && Boolean(sheet.webThickness ?? webThickness)

/**
 * Names the order of the values in a size string, for the small caption shown above it. The
 * value itself stays the plain "100 x 50 x 3000" string; this only says which number is which.
 * Returns the English text, which is also the translation key.
 * @param {Object} shape
 * @param {boolean} [shape.isCircle] - A circle reads diameter then length
 * @param {boolean} [shape.hasLength] - A profile adds its length after width and height
 * @returns {string} 'Ø x L', 'W x H x L' or 'W x H'
 */
export const getSizeCaptionKey = ({ hasLength, isCircle } = {}) => {
    if (isCircle) return 'Ø x L'
    return hasLength ? 'W x H x L' : 'W x H'
}

/**
 * Builds the composite key that keeps two IBeam records with the same flange thickness but
 * different web thickness as distinct thickness-dropdown options (QM-3717). Non-IBeam profiles
 * key on thickness alone, unchanged from the pre-QM-3717 single-value behaviour.
 * @param {Object} record - Sheet/record object (profile, thickness, webThickness)
 * @returns {string} A primitive suitable for a MUI Select value; '' when there is no thickness
 */
export const getThicknessOptionValue = ({ profile, thickness, webThickness } = {}) => {
    if (!thickness) return ''
    return profile === PROFILE_TYPES.I_BEAM ? `${webThickness ?? ''}|${thickness}` : String(thickness)
}

/**
 * Decodes a profile dimension string back onto quote-item fields. Inverse of
 * getPartDimensionsDisplay — the string is always "Width x Height" for every profile
 * except Circle (single diameter) and Square (single side).
 * @param {string} profile - Profile type (e.g. 'Angle', 'Square', 'Circle')
 * @param {string} value - Encoded dimension string
 * @returns {Object} Partial quote item fields to merge onto the item
 */
export const parseProfileDimensions = (profile, value) => {
    switch (profile?.toLowerCase()) {
        case 'circle':
            return { diameter: parseFloat(value) || 0 }
        case 'square':
            return { rotaryProfileWidth: parseFloat(value) || 0 }
        default: {
            const [width, length] = (value || '').split(' x ').map((v) => parseFloat(v) || 0)
            return { rotaryProfileWidth: width, rotaryProfileLength: length }
        }
    }
}

/**
 * Validates if profile types are compatible for fitting check
 * @param {Object} sheet - Sheet object with profile information
 * @param {Object} partDimensions - Part dimensions with profile information
 * @returns {boolean} True if profiles are compatible
 */
const validateProfileCompatibility = (sheet, partDimensions) => {
    if (!sheet || !partDimensions) {
        return false
    }

    // Check if profiles match when both are defined
    if (sheet.profile && partDimensions.profile && sheet.profile !== partDimensions.profile) {
        return false
    }

    return true
}

/**
 * Converts part dimensions to organisation's unit system
 * @param {Object} partDimensions - Original part dimensions
 * @param {Object} organisation - Organisation settings
 * @returns {Object} Converted part dimensions
 */
const convertPartDimensions = (partDimensions, organisation) => {
    const orgUsesImperial = organisation.defaultDrawingUnits.toLowerCase() === 'imperial'
    const orgAndPartUnitsAreEqual = partDimensions.isImperial === orgUsesImperial

    if (orgAndPartUnitsAreEqual) {
        return partDimensions
    }

    const convertValue = (value) => {
        if (!value) return value
        return orgUsesImperial ? mmToInches(value) : inchesToMm(value)
    }

    return {
        ...partDimensions,
        rotaryProfileWidth: convertValue(partDimensions.rotaryProfileWidth),
        rotaryProfileLength: convertValue(partDimensions.rotaryProfileLength),
        diameter: convertValue(partDimensions.diameter),
        displayBoundBoxHeight: convertValue(partDimensions.displayBoundBoxHeight),
        displayBoundBoxWidth: convertValue(partDimensions.displayBoundBoxWidth),
    }
}

/**
 * Determines the effective width, height and diameter for fitting calculations
 * @param {Object} partDimensions - Part dimensions object
 * @param {Object} convertedDimensions - Converted part dimensions
 * @returns {Object} Object with width, height and diameter properties
 */
const getEffectiveDimensions = (partDimensions, convertedDimensions) => {
    return {
        width: partDimensions?.profile
            ? convertedDimensions.rotaryProfileWidth
            : convertedDimensions.displayBoundBoxWidth,
        height: partDimensions?.profile
            ? convertedDimensions.rotaryProfileLength
            : convertedDimensions.displayBoundBoxHeight,
        diameter: convertedDimensions.diameter,
    }
}

/**
 * Checks if a part fits on a sheet based on profile type
 * @param {Object} sheet - Sheet object
 * @param {Object} effectiveDimensions - Effective width/height/diameter for fitting
 * @param {Object} organisation - Organisation settings
 * @returns {boolean} True if part fits on sheet
 */
const checkProfileFit = (sheet, effectiveDimensions, organisation) => {
    const { diameter, height, width } = effectiveDimensions

    switch (sheet.profile?.toLowerCase()) {
        case 'square':
            return isDimensionWithinTolerance(sheet.sheetWidth, width, organisation, true)
        case 'circle':
            return isDimensionWithinTolerance(sheet.diameter, diameter, organisation, true)
        case 'flatsidedoval':
        case 'angle':
        case 'uchannel':
        case 'ibeam':
        case 'tbeam':
            // Open-section profiles have a fixed orientation (unlike rectangle), so width and
            // height must each match without the either-orientation swap below.
            return (
                isDimensionWithinTolerance(sheet.sheetWidth, width, organisation, true) &&
                isDimensionWithinTolerance(sheet.sheetHeight, height, organisation, true)
            )
        case 'rectangle':
            // Rectangle can fit in either orientation
            return (
                (isDimensionWithinTolerance(sheet.sheetWidth, width, organisation, true) &&
                    isDimensionWithinTolerance(sheet.sheetHeight, height, organisation, true)) ||
                (isDimensionWithinTolerance(sheet.sheetHeight, width, organisation, true) &&
                    isDimensionWithinTolerance(sheet.sheetWidth, height, organisation, true))
            )
        default:
            return (
                doesPartFitWithinTolerance(sheet.sheetHeight, width, organisation) &&
                doesPartFitWithinTolerance(sheet.sheetWidth, height, organisation)
            )
    }
}

/**
 * Determines if a profile size fits on a sheet
 * @param {Object} sheet - Sheet object with dimensions and profile
 * @param {Object} partDimensions - Part dimensions object
 * @param {Object} organisation - Organisation settings
 * @returns {boolean} True if part fits on sheet
 */
export const doesProfileSizeFitSheet = (sheet, partDimensions, organisation) => {
    // Validate inputs and profile compatibility
    if (!validateProfileCompatibility(sheet, partDimensions)) {
        return false
    }

    // Handle case where sheet has no profile (flat parts)
    if (!sheet.profile) {
        // TODO: Implement proper flat part fitting logic
        // Currently returning true as a placeholder
        return true
    }

    // Convert part dimensions to organisation units
    const convertedDimensions = convertPartDimensions(partDimensions, organisation)

    // Get effective dimensions for fitting calculations
    const effectiveDimensions = getEffectiveDimensions(partDimensions, convertedDimensions)

    // Check if part fits based on profile type
    return checkProfileFit(sheet, effectiveDimensions, organisation)
}

/**
 * Checks if part thickness is within sheet thickness tolerance
 * @param {Object} sheet - Sheet object
 * @param {number} partThickness - Part thickness value
 * @param {Object} partDimensions - Part dimensions object
 * @param {Object} organisation - Organisation settings
 * @param {boolean} usePercentage - Whether to use percentage tolerance
 * @returns {boolean} True if thickness is within tolerance
 */
export const isThicknessWithinTolerance = (
    sheet,
    partThickness,
    partDimensions,
    organisation,
    usePercentage = false
) => {
    const orgUsesImperial = organisation.defaultDrawingUnits.toLowerCase() === 'imperial'
    const orgAndPartUnitsAreEqual = partDimensions.isImperial === orgUsesImperial
    const convertedPartThickness = orgAndPartUnitsAreEqual
        ? partThickness
        : orgUsesImperial
          ? mmToInches(partThickness)
          : inchesToMm(partThickness)

    return isDimensionWithinTolerance(sheet.thickness, convertedPartThickness, organisation, usePercentage)
}

/**
 * Checks if a dimension is within organisation tolerance
 * @param {number} sheetDimension - Sheet dimension value
 * @param {number} partDimension - Part dimension value
 * @param {Object} organisation - Organisation settings
 * @param {boolean} usePercentage - Whether to use percentage tolerance
 * @returns {boolean} True if dimension is within tolerance
 */
const isDimensionWithinTolerance = (sheetDimension, partDimension, organisation, usePercentage = false) => {
    const precision = organisationLengthPrecision(organisation)
    if (usePercentage) {
        return areNumbersWithinTolerance(
            partDimension?.toFixed(precision),
            sheetDimension?.toFixed(precision),
            PROFILE_SIZE_TOLERANCE_PERCENTAGE
        )
    } else {
        return (
            Math.abs(partDimension?.toFixed(precision) - sheetDimension?.toFixed(precision)) <=
            organisationDimensionTolerance(organisation)
        )
    }
}

/**
 * Checks if part dimension fits within sheet dimension with tolerance
 * @param {number} sheetDimension - Sheet dimension value
 * @param {number} partDimension - Part dimension value
 * @param {Object} organisation - Organisation settings
 * @returns {boolean} True if part fits within tolerance
 */
const doesPartFitWithinTolerance = (sheetDimension, partDimension, organisation) => {
    const precision = organisationLengthPrecision(organisation)
    return (
        partDimension?.toFixed(precision) - sheetDimension?.toFixed(precision) <=
        organisationDimensionTolerance(organisation)
    )
}
